import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import "../styles/bundle.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bundle = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const cancelled = searchParams.get("checkout") === "cancelled";

  useEffect(() => {
    document.title = "The Disciplined Mind, E-Book & Workbook Bundle | Disciplined Me";
  }, []);

  const handleCheckout = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Checkout isn't available right now. Please try again shortly."
      );
      setLoading(false);
    }
  };

  return (
    <div className="site-page page-bundle">
      <nav><div className="wrap"><div className="logo">disciplined<span>.me</span></div><Link to="/" className="nav-back">← Back home</Link></div></nav>

      <section className="hero">
        <div className="wrap">
          {cancelled && (
            <div className="cancel-banner">Checkout cancelled, you can try again anytime.</div>
          )}
          {error && <div className="cancel-banner">{error}</div>}
          <span className="bundle-badge">E-book + Workbook bundle</span>
          <h1>You don't need to feel ready.<br />You need <span className="hl">a system</span> that works even when you don't.</h1>
          <p className="hero-sub">The Disciplined Mind e-book and companion workbook, everything you need to stop restarting the same goals and start finishing them, using AI as your daily coach.</p>

          <div className="price-card" style={{ marginTop: "6px" }}>
            <div className="price-new">CHF 29</div>
            <div className="price-label">Bundle price · E-Book + Workbook · Instant download</div>
            <button className="price-btn" onClick={handleCheckout} disabled={loading}>
              {loading ? "Redirecting to checkout…" : "Get The Disciplined Mind, CHF 29"}
            </button>
            <div className="price-micro">Instant download. Start tonight.</div>
          </div>
        </div>
      </section>

      <section className="story">
        <div className="wrap">
          <div className="story-box">
            <div className="eyebrow">Why I wrote this</div>
            <p>I lost my job while recovering from knee surgery. Not because of anything I did, I was just gone too long, and the world moved on without me. Lying on that couch, I realized something worse than losing the job: I had never actually learned how to achieve a goal. I had set plenty of them. I had just never built anything that survived contact with a bad week.</p>
            <p className="lora">What I built afterward, through failure, testing, and hundreds of honest conversations with AI, became The Disciplined Mind. It's not a motivational book. It's a system.</p>
          </div>
        </div>
      </section>

      <section className="bundle-contents">
        <div className="wrap">
          <div className="contents-head">
            <div className="eyebrow" style={{ color: "var(--purple-deep)" }}>What you get</div>
            <h2>Two pieces. One complete system.</h2>
          </div>
          <div className="content-grid">
            <div className="content-card">
              <span className="tag book">The E-Book</span>
              <h3>The Disciplined Mind</h3>
              <ul>
                <li>5 full chapters, Willpower Lie to your 30-Day Action Plan</li>
                <li>12+ ready-to-use AI prompts built into every chapter</li>
                <li>The complete framework for AI as a daily coach</li>
              </ul>
            </div>
            <div className="content-card">
              <span className="tag wb">The Workbook</span>
              <h3>The Companion Workbook</h3>
              <ul>
                <li>9 guided exercises, one per chapter concept</li>
                <li>Your own System Prompt, built step by step</li>
                <li>Full 30-day tracker + Never Miss Twice Contract</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="pros">
        <div className="wrap">
          <div className="pros-head">
            <div className="eyebrow" style={{ color: "var(--teal)" }}>Why this bundle</div>
            <h2>Two books. Two different jobs.</h2>
          </div>
          <div className="pros-cols">
            <div className="pros-col">
              <h3>Why the e-book</h3>
              <div className="pro-item"><h4>Built on a real system</h4><p>Every chapter exists because it solved a real problem in a real rebuild, not because it sounded good in a content calendar.</p></div>
              <div className="pro-item"><h4>AI is the coach, not a gimmick</h4><p>Exact prompts included, how to configure it, what to say, how to use it daily.</p></div>
              <div className="pro-item"><h4>Respects your time</h4><p>Five chapters, no padding. Read it in one sitting, start that same evening.</p></div>
            </div>
            <div className="pros-col">
              <h3>Why the workbook</h3>
              <div className="pro-item"><h4>Reading isn't doing</h4><p>The workbook turns "good book" into "I have a system", every exercise maps to a chapter.</p></div>
              <div className="pro-item"><h4>Your system lives here</h4><p>Your System Prompt and identity statement become real once written in your own words.</p></div>
              <div className="pro-item"><h4>Holds you accountable</h4><p>The 30-day tracker gives you a number to beat next month, not just a memory of reading.</p></div>
            </div>
          </div>
        </div>
      </section>

      <section className="why">
        <div className="wrap">
          <div className="why-head">
            <div className="eyebrow" style={{ color: "var(--coral)" }}>Why discipline, specifically</div>
            <h2>Universal reasons this matters, whatever your goal is</h2>
          </div>
          <div className="why-list">
            <div className="why-row"><div className="why-mark">01</div><div><h4>The only skill that transfers to everything</h4><p>Motivation is goal-specific and runs out. Discipline, built as a system, works whether you're getting fit, building a business, or finishing a degree.</p></div></div>
            <div className="why-row"><div className="why-mark">02</div><div><h4>The difference between talking and becoming</h4><p>Not because disciplined people are smarter, because they built a structure that doesn't depend on how they feel that day.</p></div></div>
            <div className="why-row"><div className="why-mark">03</div><div><h4>It compounds silently</h4><p>The gap between 80% consistency and 20% consistency over a year isn't 4x, it's a transformed life versus the same conversation twelve months later.</p></div></div>
            <div className="why-row"><div className="why-mark">04</div><div><h4>It protects your worst days</h4><p>Everyone has bad weeks. This is what stops a bad week from becoming a bad month, and a bad month from becoming starting over.</p></div></div>
            <div className="why-row"><div className="why-mark">05</div><div><h4>Buildable at any starting point</h4><p>You don't need to already be disciplined. It's not a trait you're born with, it's a system you construct, one small proof at a time.</p></div></div>
          </div>
        </div>
      </section>

      <section className="whofor">
        <div className="wrap">
          <div className="whofor-col for">
            <div className="eyebrow">Who this is for</div>
            <p>Students who know good intentions don't save a semester. Young professionals watching everyone else move forward while they feel stuck. Anyone who's read enough productivity books and just wants something that works on the bad days too.</p>
          </div>
          <div className="whofor-col not">
            <div className="eyebrow">Who this isn't for</div>
            <p>If you're looking for a quick motivational hit that fades by Thursday, this isn't it. This is for people ready to build something that actually lasts.</p>
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="wrap">
          <div className="faq-head">
            <div className="eyebrow" style={{ color: "var(--purple-deep)" }}>Common questions</div>
            <h2>Before you decide</h2>
          </div>
          <div className="faq-list">
            <div className="faq-item"><h4>Do I need ChatGPT Plus or a paid AI tool?</h4><p>No. Every prompt works with the free version of ChatGPT, Claude, Gemini, or any AI chat tool you already have.</p></div>
            <div className="faq-item"><h4>How long will this take me?</h4><p>The e-book reads in one sitting, most people finish in under two hours. The workbook is designed to be worked through across your first 30 days, not rushed.</p></div>
            <div className="faq-item"><h4>I've read productivity books before and nothing stuck. Why would this be different?</h4><p>Because this isn't a book of ideas, it's a book of prompts and exercises. You're asked to open an AI chat and use it, starting today.</p></div>
            <div className="faq-item"><h4>Is this just the free guide with extra pages?</h4><p>No. The free guide is a starting toolkit. The e-book and workbook are the complete system, the full framework, the science, the 30-day plan, and the workbook that makes it real.</p></div>
          </div>
        </div>
      </section>

      <section className="price" id="price">
        <div className="wrap">
          <button className="hero-btn" onClick={handleCheckout} disabled={loading}>
            {loading ? "Redirecting to checkout…" : "Get the bundle, CHF 29"}
          </button>
          <div className="hero-micro">Instant download · Start tonight</div>
          <p className="closing lora">"You already know what to do. This just makes sure you actually do it, even on the day you don't feel like it."</p>
        </div>
      </section>

      <footer>© Disciplined Me, Building the identity of someone who keeps going.</footer>
    </div>
  );
};

export default Bundle;
